import { AsYouType } from 'libphonenumber-js';
import { v4 as uuidv4 } from 'uuid';
import { IVehicleBase, IAddress } from "../classes/Domain";
import { PATHS } from "../AppConstants";

class Utilities {
    static format(input: string, ...values: string[]) {
        for (let i = 0; i < values.length; i++) {
            const regex = new RegExp('\\{' + i + '\\}');
            input = input.replace(regex, values[i]);
        }
        return input;
    }

    static trimLeft (str: string, charList: string) {
        if (charList === undefined)
            charList = "\s";

        return str.replace(new RegExp("^[" + charList + "]+"), "");
    };

    static trimRight(str: string, charList: string) {
        if (charList === undefined)
            charList = "\s";

        return str.replace(new RegExp("[" + charList + "]+$"), "");
    };

    static getHomePageUrl() {
        return PATHS.START;
    }

    static formatUrl(urlTemplate: string, ...values: string[]) {
        return Utilities.trimRight(Utilities.format(urlTemplate, ...values), "/");
    }

    static formatUrlWithPrefix(prefix: string, urlTemplate: string, ...values: string[]) {
        const t = `/${Utilities.trimLeft(prefix, "/")}/${Utilities.trimLeft(urlTemplate, "/")}`;
        return Utilities.trimRight(Utilities.format(t, ...values), "/");
    }

    static getEmptyRejectedPromise() {
        return Promise.reject().catch();
    }

    static parseDigits(str: string) {
        return (str.match(/\d+/g) || []).join('');
    };

    static parseDate(dateStr: string) {
        if (dateStr === undefined || !dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return null;
        const [m, d, y] = dateStr.split('/');

        const month = parseInt(m, 10);
        const day = parseInt(d, 10);
        const year = parseInt(y, 10);

        return new Date(year, month - 1, day);
    }

    static formatDate(value : string) {
        const digits = Utilities.parseDigits(value);
        const chars = digits.split('');
        return chars
            .reduce(
                (r, v, index) => (index === 2 || index === 4 ? `${r}/${v}` : `${r}${v}`),
                ''
            )
            .substr(0, 10);
    };

    static formatCreditCardExpiration(value: string) {
        const digits = Utilities.parseDigits(value);
        const chars = digits.split('');
        return chars
            .reduce(
                (accumulator, currentValue, index) => (index === 2 ? `${accumulator}/${currentValue}` : `${accumulator}${currentValue}`),
                ''
            )
            .substr(0, 5);
    };

    static formatDateObj(date: Date) {
        return (date.getMonth() + 1) + "/" + date.getDate() + "/" + date.getFullYear();
    }

    static formatPhone(value: string) {
        const digits = Utilities.parseDigits(value).substr(0, 10);
        if (digits.startsWith("0") || digits.startsWith("1")) return "";
        return new AsYouType('US').input(digits);
    };

    static formatInteger(str: string) {
        if (!str) return "";
        const r = parseInt(str.replace(/[^\d]+/gi, ''), 10);
        return r ? r.toString() : '';
    }

    static formatNumber(val: any) {
        const r = parseInt(val.toString().replace(/[^\d]+/gi, ''), 10);
        return r ? r.toLocaleString("en") : '';
    }

    static formatCurrency(val: any) {
        if (null === val) return "";
        const r = parseFloat(val.toString().replace(/[^\d\.]+/gi, ''));
        return "$" + r.toLocaleString("en", { 'minimumFractionDigits': 2, 'maximumFractionDigits': 2 });
    }

    static formatVehicle(vehicle: IVehicleBase) {
        return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    }

    static uniqueId() {
        return uuidv4();
    }

    static getStringValueSafe(obj: any, property: string) {
        return obj
            ? null === obj[property] ? "" : obj[property]
            : "";
    }

    static isFullZip(zip: string) {
        return !!zip && 5 === zip.length;
    }

    static isAddressFilled(address: IAddress) : boolean {
        return address
            && !!address.street
            && !!address.city
            && !!address.stateCode
            && !!address.zipCode
            && Utilities.isFullZip(address.zipCode);
    }

    static formatAddress(address: IAddress | null): string {
        if (!address) return "";
        if (!Utilities.isAddressFilled(address)) return "";

        return address.street
            + (!!address.apartment ? ", " + address.apartment : "")
            + ", " + address.city
            + ", " + address.stateCode
            + " " + address.zipCode;
    }

    static appendUrlSuffix(baseUrl: string, suffix: string): string {
        return baseUrl.replace(/$\//, "") + "/" + suffix.replace(/^\//, "");
    }

    static isObject(data: any) {
        return typeof data === "object" && null !== data;
    }

    static getAge(birthDate: Date) {
        var today = new Date();
        var age = today.getFullYear() - birthDate.getFullYear();
        var m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }

    static getFormattedDateTime(): string {
        const now = new Date();

        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');

        return `${year}${month}${day}${hours}${minutes}${seconds}`;
    }
}

export default  Utilities;